if (!customElements.get('product-quick-popup')) {
  customElements.define(
    'product-quick-popup',
    class ProductQuickPopup extends HTMLElement {
      constructor() {
        super();
        this.closeButton = this.querySelector('.product-quick-popup__close');
        this.content = this.querySelector('.product-quick-popup__body');
        this.productIds = JSON.parse(this.dataset.productIds);
        this.currency = this.dataset.currency;
        this.outsideClickListener = null;
        this.observer = null;
        this.packPriceObservers = [];
        this.packPriceSyncInterval = null;
        this.packPriceSyncTimeout = null;
        this.delegatedClickListener = null;
      }

      connectedCallback() {
        // Document-level delegation so dynamically re-injected .pack-card nodes
        // (e.g. after cart:updated rerenders the section) still respond to clicks.
        this.delegatedClickListener = (e) => {
          const card = e.target.closest('.pack-card');
          if (card) this.openPopup(e);
        };
        document.addEventListener('click', this.delegatedClickListener);

        this.closeButton.addEventListener('click', this.close.bind(this));
        document.addEventListener('cart:updated', this.close.bind(this));
      }

      openPopup(e) {
        const packCard = e.target.closest('.pack-card');
        if (!packCard) return;

        const productUrl = packCard.dataset.productUrl;
        if (!productUrl) return;

        // Hard-coded PREORDER message (edit as needed)
        const HARD_CODED_MESSAGE = `
          Note: This is a PREORDER. It allows you to get in line for the product so you get it first 
          when it gets back in stock. The whole order will be held until the entire order is in stock 
          to ship together. Items will be paid for at time of checkout and Items will ship once in stock. 
          Email us if you want to pay up and separate the order. <br>• <strong>Shipping:</strong> 4-6 Weeks
        `;

        // Show loading UI (spinner)
        const imageWrapper = packCard.querySelector('.pack-card-image-wrapper');
        if (imageWrapper) {
          imageWrapper.classList.add('loading');
          const spinner = imageWrapper.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        // Fetch product page HTML
        fetch(productUrl)
          .then(response => response.text())
          .then(responseText => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) return;

            // Prevent the product form from updating the URL
            productElement.setAttribute('data-update-url', 'false');

            // Inject product HTML into the popup content
            HTMLUpdateUtility.setInnerHTML(this.content, productElement.outerHTML);
            this.initializeInjectedProduct(productElement, responseHTML);

            // Show the popup
            document.body.classList.add('overflow-hidden');
            this.classList.add('open');

            // Attach outside-click listener after popup is open.
            // This listener checks if the click is outside the element with class 'product-quick-popup__content'
            this.outsideClickListener = (event) => {
              const popupContent = this.querySelector('.product-quick-popup__content');
              if (popupContent && !popupContent.contains(event.target)) {
                this.close();
              }
            };
            document.addEventListener('click', this.outsideClickListener);

            // Find the "Add to Cart" button inside the popup
            const addToCartButton = this.content.querySelector('.product-form__submit');
            if (!addToCartButton) return;

            const preOrderContainerPDP = document.getElementById("display-pre-order-message");
            if (preOrderContainerPDP) preOrderContainerPDP.style.display = "none";

            // Find (or create) the #display-pre-order-message-2 container
            const preOrderContainer = this.content.querySelector('#display-pre-order-message-2');
            if (preOrderContainer) {
              preOrderContainer.style.display = 'none';
              preOrderContainer.innerHTML = '';
            }

            // Function to check if button text is "Pre-order" and then show/hide the container
            function checkPreOrder() {
              if (addToCartButton.textContent.trim() === 'Pre-order' && preOrderContainer) {
                preOrderContainer.innerHTML = HARD_CODED_MESSAGE;
                preOrderContainer.style.display = 'block';
              } else if (preOrderContainer) {
                preOrderContainer.innerHTML = '';
                preOrderContainer.style.display = 'none';
              }
            }

            // Initial check
            checkPreOrder();

            // Observe button changes (for variant changes, etc.)
            // Stored on instance so close() can disconnect — prevents memory leak
            // on repeated open/close cycles.
            if (this.observer) {
              this.observer.disconnect();
            }
            this.observer = new MutationObserver(checkPreOrder);
            this.observer.observe(addToCartButton, { childList: true, subtree: true });

            // Override default Add to Cart form submission
            addToCartButton.addEventListener('click', event => {
              event.preventDefault();

              const form = addToCartButton.closest('form');
              if (!form) return;
              const formData = new FormData(form);

              fetch('/cart/add.js', {
                method: 'POST',
                body: formData
              })
              .then(res => res.json())
              .then(cart => {
                console.log('Product added to cart:', cart);
                setTimeout(() => {
                  this.close();
                  fetch('/cart.js')
                    .then(response => response.json())
                    .then(data => {
                      console.log("Data==>", data);
                      let totalItems = data.items.reduce((sum, item) => sum + item.quantity, 0);
                      const cartCountSpan = document.querySelector(".cart-count-bubble > span");
                      if (cartCountSpan) {
                        cartCountSpan.innerHTML = totalItems;
                      } else {
                        const headerCartIcon = document.querySelector('.header__icon--cart');
                        if (headerCartIcon) {
                          let newElement = document.createElement("div");
                          newElement.className = "cart-count-bubble";
                          newElement.innerHTML = `<span>${totalItems}</span>`;
                          headerCartIcon.appendChild(newElement);
                        }
                      }
                    })
                    .catch(err => console.log("Error detected==>", err));
                    document.dispatchEvent(new Event('cart:updated'));
                }, 700);
              })
              .catch(error => {
                console.error('Error adding product to cart:', error);
              });
            });

            // Refresh Preorder app if needed
            if (window.DashPreorders && typeof window.DashPreorders.refresh === 'function') {
              window.DashPreorders.refresh();
              console.log('Preorder app refreshed');
            }
          })
          .finally(() => {
            // Hide loading UI
            if (imageWrapper) {
              imageWrapper.classList.remove('loading');
              const spinner = imageWrapper.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            }
          });
      }

      initializeInjectedProduct(productElement, responseHTML) {
        const productInfo = this.content.querySelector('product-info') || productElement || this.content;
        this.initializePopupPackPricing(productInfo);
        [100, 500, 1000, 2000].forEach((delay) => {
          window.setTimeout(() => this.initializePopupPackPricing(productInfo), delay);
        });

        this.ensureSizingAIScript(responseHTML).then(() => {
          if (window.ApollaSizingAI && typeof window.ApollaSizingAI.init === 'function') {
            window.ApollaSizingAI.init(productInfo);
          }
        });
      }

      ensureSizingAIScript(responseHTML) {
        if (window.ApollaSizingAI && typeof window.ApollaSizingAI.init === 'function') {
          return Promise.resolve();
        }

        if (ProductQuickPopup.sizingAIScriptPromise) {
          return ProductQuickPopup.sizingAIScriptPromise;
        }

        const script = responseHTML && responseHTML.querySelector('script[data-snippets*="sizing-ai-prompt"]');
        const src = script && script.getAttribute('src');
        if (!src) return Promise.resolve();

        const absoluteSrc = new URL(src, window.location.origin).href;
        const existing = Array.from(document.scripts).find((candidate) => candidate.src === absoluteSrc);
        if (existing) {
          ProductQuickPopup.sizingAIScriptPromise = new Promise((resolve) => {
            if (window.ApollaSizingAI && typeof window.ApollaSizingAI.init === 'function') {
              resolve();
              return;
            }
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', resolve, { once: true });
          });
          return ProductQuickPopup.sizingAIScriptPromise;
        }

        ProductQuickPopup.sizingAIScriptPromise = new Promise((resolve) => {
          const loader = document.createElement('script');
          loader.src = absoluteSrc;
          loader.defer = true;
          if (!document.getElementById('snippets-script')) {
            loader.id = 'snippets-script';
          }
          loader.dataset.snippets = script.getAttribute('data-snippets') || 'sizing-ai-prompt';
          loader.dataset.apollaPopupSizingAi = 'true';
          loader.addEventListener('load', resolve, { once: true });
          loader.addEventListener('error', resolve, { once: true });
          document.head.appendChild(loader);
        });

        return ProductQuickPopup.sizingAIScriptPromise;
      }

      clearPopupPackPriceSync() {
        if (this.packPriceSyncInterval) {
          window.clearInterval(this.packPriceSyncInterval);
          this.packPriceSyncInterval = null;
        }
        if (this.packPriceSyncTimeout) {
          window.clearTimeout(this.packPriceSyncTimeout);
          this.packPriceSyncTimeout = null;
        }
      }

      queuePopupPackPriceSync(productInfo) {
        const scope = productInfo || this.content.querySelector('product-info') || this.content;
        this.clearPopupPackPriceSync();
        this.syncCurrentPopupPackPrice(scope);

        this.packPriceSyncInterval = window.setInterval(() => {
          this.syncCurrentPopupPackPrice(scope);
        }, 100);
        this.packPriceSyncTimeout = window.setTimeout(() => {
          this.clearPopupPackPriceSync();
        }, 1800);
      }

      syncCurrentPopupPackPrice(productInfo) {
        const scope = productInfo || this.content.querySelector('product-info') || this.content;
        const packSelector = scope.querySelector('custom-pack-selector');
        if (!packSelector) return false;

        const selectedInput = packSelector.querySelector('.custom-pack-input:checked');
        if (!selectedInput) return false;

        const qty = Number(selectedInput.value || selectedInput.dataset.quantity || 1);
        const quantityLabel = packSelector.querySelector('.custom-pack-label strong');
        const quantityInput = scope.querySelector('.product-form__quantity .quantity__input');
        const regularPriceBox = scope.querySelector('.cuspric-box');
        const packageBoxes = Array.from(scope.querySelectorAll('.cus-package-box-main'));
        const packageBox = packageBoxes.find((box) => (
          regularPriceBox && (regularPriceBox.compareDocumentPosition(box) & Node.DOCUMENT_POSITION_FOLLOWING)
        )) || packageBoxes[0];

        if (!packageBox) return false;

        const basePrice = Number(packageBox.dataset.basePrice || 0) / 100;
        if (!basePrice) return false;

        const formatMoney = (amount) => `$${amount.toFixed(2)}`;
        const hideDuplicatePackageBoxes = () => {
          packageBoxes.forEach((box) => {
            if (box === packageBox) return;
            box.classList.add('apolla-popup-package-price-hidden');
            box.classList.remove('apolla-popup-package-price-active');
            box.style.display = 'none';
          });
        };

        if (quantityLabel) quantityLabel.textContent = `${qty}x`;
        if (quantityInput) quantityInput.value = qty;

        packageBox.classList.add('apolla-popup-package-price');
        hideDuplicatePackageBoxes();

        if (qty === 1) {
          this.classList.remove('popup-pack-multi');
          packageBox.classList.remove('apolla-popup-package-price-active');
          packageBox.style.display = 'none';
          if (regularPriceBox) regularPriceBox.style.display = 'block';
          return true;
        }

        const discountByQty = { 4: 25, 5: 27, 7: 30 };
        const discountPercentage = discountByQty[qty] || 0;
        const totalPrice = basePrice * qty;
        const discountedTotal = totalPrice * (1 - discountPercentage / 100);
        const discountedPerPair = basePrice * (1 - discountPercentage / 100);

        this.classList.add('popup-pack-multi');
        packageBox.classList.add('apolla-popup-package-price-active');
        packageBox.classList.remove('apolla-popup-package-price-hidden');
        packageBox.style.display = 'block';
        if (regularPriceBox) regularPriceBox.style.display = 'none';

        const priceDisplay = packageBox.querySelector('.price-display, .total-withoutdiscountprice-display, #price-display');
        const perPairDisplay = packageBox.querySelector('.looprice-display, #looprice-display');
        const discountedPriceDisplay = packageBox.querySelector('#discounted-price-display');
        const discountDisplay = packageBox.querySelector('.discount-percentage-cus');

        if (priceDisplay) priceDisplay.textContent = formatMoney(totalPrice);
        if (perPairDisplay) perPairDisplay.textContent = formatMoney(discountedPerPair);
        if (discountedPriceDisplay) discountedPriceDisplay.textContent = discountedTotal.toFixed(2);
        if (discountDisplay) discountDisplay.textContent = `${discountPercentage}% Discount Applied!`;

        return true;
      }

      initializePopupPackPricing(productInfo) {
        const packSelector = productInfo.querySelector('custom-pack-selector');
        if (!packSelector || packSelector.dataset.popupPackInitialized === 'true') return;

        const regularPriceBox = productInfo.querySelector('.cuspric-box');
        const packageBoxes = Array.from(productInfo.querySelectorAll('.cus-package-box-main'));
        const getPackageBox = () => {
          const currentBoxes = Array.from(productInfo.querySelectorAll('.cus-package-box-main'));
          return currentBoxes.find((box) => (
            regularPriceBox && (regularPriceBox.compareDocumentPosition(box) & Node.DOCUMENT_POSITION_FOLLOWING)
          )) || currentBoxes[0] || null;
        };
        const hideDuplicatePackageBoxes = (activeBox) => {
          productInfo.querySelectorAll('.cus-package-box-main').forEach((box) => {
            if (box === activeBox) return;
            box.classList.add('apolla-popup-package-price-hidden');
            box.classList.remove('apolla-popup-package-price-active');
            box.style.display = 'none';
          });
        };

        let packageBox = getPackageBox();
        if (!packageBox) return;
        packageBox.classList.add('apolla-popup-package-price');
        hideDuplicatePackageBoxes(packageBox);

        let pendingPackagePriceRefresh = false;

        const getSelectedQty = () => {
          const selectedInput = packSelector.querySelector('.custom-pack-input:checked');
          return selectedInput ? Number(selectedInput.value || selectedInput.dataset.quantity || 1) : 1;
        };

        const resolvePackageBox = () => {
          packageBox = getPackageBox();
          if (packageBox) {
            packageBox.classList.add('apolla-popup-package-price');
            hideDuplicatePackageBoxes(packageBox);
          }
          return packageBox;
        };

        const schedulePackPriceRefresh = () => {
          if (pendingPackagePriceRefresh) return;
          pendingPackagePriceRefresh = true;
          this.queuePopupPackPriceSync(productInfo);
          [50, 150, 300, 700, 1200].forEach((delay) => {
            window.setTimeout(() => {
              this.queuePopupPackPriceSync(productInfo);
              if (delay === 1200) pendingPackagePriceRefresh = false;
            }, delay);
          });
        };

        const observePackSelector = () => {
          if (packSelector.dataset.popupSelectorObserved === 'true') return;

          packSelector.dataset.popupSelectorObserved = 'true';
          const selectorObserver = new MutationObserver(() => {
            schedulePackPriceRefresh();
          });

          selectorObserver.observe(packSelector, {
            attributes: true,
            attributeFilter: ['checked', 'class', 'style'],
            childList: true,
            characterData: true,
            subtree: true,
          });
          this.packPriceObservers.push(selectorObserver);
        };

        const observeProductInfoForPackReplacement = () => {
          if (productInfo.dataset.popupPackReplacementObserved === 'true') return;

          productInfo.dataset.popupPackReplacementObserved = 'true';
          const productInfoObserver = new MutationObserver(() => {
            const currentPackSelector = productInfo.querySelector('custom-pack-selector');
            if (currentPackSelector && currentPackSelector.dataset.popupPackInitialized !== 'true') {
              this.initializePopupPackPricing(productInfo);
            }
          });

          productInfoObserver.observe(productInfo, {
            childList: true,
            subtree: true,
          });
          this.packPriceObservers.push(productInfoObserver);
        };

        if (this.packPriceObservers.length) {
          this.packPriceObservers.forEach((priceObserver) => priceObserver.disconnect());
          this.packPriceObservers = [];
          productInfo.removeAttribute('data-popup-pack-replacement-observed');
        }

        const updatePackPrice = () => {
          this.queuePopupPackPriceSync(productInfo);
        };

        ['click', 'input', 'change'].forEach((eventName) => {
          packSelector.addEventListener(eventName, (event) => {
            if (!event.target.closest('.custom-pack-input, .custom-pack-button, label')) return;
            schedulePackPriceRefresh();
          });
        });

        packSelector.dataset.popupPackInitialized = 'true';
        observePackSelector();
        observeProductInfoForPackReplacement();
        updatePackPrice();
      }

      close() {
        this.classList.remove('popup-pack-multi');
        document.body.classList.remove('overflow-hidden');
        this.classList.remove('open');
        if (this.outsideClickListener) {
          document.removeEventListener('click', this.outsideClickListener);
          this.outsideClickListener = null;
        }
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.packPriceObservers.length) {
          this.packPriceObservers.forEach((priceObserver) => priceObserver.disconnect());
          this.packPriceObservers = [];
        }
        this.clearPopupPackPriceSync();
      }
    }
  );
}
